import { Alert, Box, Chip, Stack, Typography } from "@mui/material"
import { FC } from "react"
import { ReservationSyncDto } from "src/features/things/reservationSyncApi"

/**
 * The confirmation, recorded (LOCKER-421 `DoD6`, `DoD8`).
 *
 * The sync is operated from a shell and this console can neither propose, confirm,
 * apply nor revert one. What it can do is show the RECORD: what the operator was
 * asked, what they typed, when, and the checksum binding the plan they saw to the
 * writes that landed.
 *
 * The checksum is the part that earns its place. Without it, "these are the writes
 * that were confirmed" is a narrative — the ledger below could be anything, and a
 * reader has no way to tell whether the plan changed between the prompt and the
 * write. With it, the claim is checkable: the same rows and the same proposed
 * values hash the same, and one extra write does not.
 *
 * A DECLINED run is shown too, and deliberately. "The operator was asked and said
 * no" is a different fact from "nothing needed doing", and only one of them is
 * worth knowing when the same locker comes back next week.
 */

const OUTCOME_LABEL: Record<string, string> = {
  confirmed: "confirmed",
  declined: "declined at the prompt",
  nonTty: "refused — stdin was not a terminal",
  emptyPlan: "never prompted — the plan was empty",
  refusedUnpublishable: "refused — the run's report could not be stored",
}

const OUTCOME_NOTE: Record<string, string> = {
  confirmed:
    "The operator typed the word against the printed plan. All or nothing: there is no per-row or per-class approval, because a prompt over thousands of rows is a prompt nobody reads.",
  declined:
    "Anything but the exact word aborts. The run exited 0 having written nothing, and its record stayed at DRIFT_ASSESSED — declining to write is not an error.",
  nonTty:
    "A pipe or a redirect cannot carry a human decision, so the gate refuses rather than defaulting either way. This is what replaced the --yes flag, which skipped the confirmation on exactly the runs nobody was watching.",
  emptyPlan:
    "Nothing diverged in a writable class, so there was nothing to confirm.",
  refusedUnpublishable:
    "The drift report could not be stored, so a write would have been unrecorded and unrevertable. An unrecorded write is the one thing the audit trail exists to prevent.",
}

export const ConfirmationRecord: FC<{ run: ReservationSyncDto }> = ({ run }) => {
  if (!run.confirmation && !run.planChecksum) return null
  const outcome = run.confirmation?.outcome ?? "confirmed"

  return (
    <Box data-ui="UI-SL2W-164" sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 2.5, mb: 3 }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.75 }}>
        <Typography sx={{ fontSize: "0.88rem", fontWeight: 600 }}>
          The confirmation, recorded
        </Typography>
        <Chip
          size="small"
          label={OUTCOME_LABEL[outcome] ?? outcome}
          color={outcome === "confirmed" ? "success" : "default"}
          variant={outcome === "confirmed" ? "filled" : "outlined"}
          sx={{ fontWeight: 600 }}
        />
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            lg: "repeat(4, minmax(0, 1fr))",
          },
          gap: 2,
        }}
      >
        {(
          [
            ["GRANULARITY", "All-or-nothing, one prompt"],
            ["TYPED", run.confirmation?.typed ?? "—"],
            [
              "CONFIRMED AT",
              run.confirmation?.confirmedAtIso?.replace("T", " ").slice(0, 19) ??
                "—",
            ],
            [
              "PLAN CHECKSUM",
              run.planChecksum ? `sha256 ${run.planChecksum.slice(0, 12)}…` : "—",
            ],
          ] as const
        ).map(([label, value]) => (
          <Box key={label}>
            <Typography
              variant="caption"
              sx={{
                color: "text.disabled",
                fontWeight: 700,
                letterSpacing: "0.08em",
              }}
            >
              {label}
            </Typography>
            <Typography
              sx={{ fontSize: "0.8rem", fontFamily: "monospace", wordBreak: "break-all" }}
            >
              {value}
            </Typography>
          </Box>
        ))}
      </Box>

      <Typography
        variant="caption"
        sx={{
          display: "block",
          mt: 1.75,
          pt: 1.5,
          borderTop: 1,
          borderColor: "divider",
          color: "text.secondary",
          lineHeight: 1.55,
        }}
      >
        {OUTCOME_NOTE[outcome]}
      </Typography>

      {outcome === "confirmed" && (
        <Alert severity="success" variant="outlined" sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            OLAP repaired itself, downstream
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
            Each DynamoDB write re-drove the stream Lambda, which rewrote that row’s
            OLAP projection through the corrected mapping — so any{" "}
            <code>knownProjectionDefect</code> row this sync touched was repaired
            without the run ever writing to Postgres.{" "}
            <strong>Rows the sync did not touch stay corrupt.</strong> This is a
            per-row consequence of writing, not a locker-wide repair.
          </Typography>
        </Alert>
      )}
    </Box>
  )
}
